import React, { useState, useMemo } from 'react';
import './SlotCalendar.css';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Local calendar-day key (never toISOString - that shifts the day near midnight)
export const dayKey = (d) => {
  const date = new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

/**
 * Month calendar marking the days that have coaching slots.
 * slots: upcoming slots (soonest first); selectedDay: 'YYYY-MM-DD' | null;
 * onSelectDay(key | null)
 */
export default function SlotCalendar({ slots, selectedDay, onSelectDay }) {
  const firstSlotDate = slots.length ? new Date(slots[0].startTime) : new Date();
  const [month, setMonth] = useState(() => new Date(firstSlotDate.getFullYear(), firstSlotDate.getMonth(), 1));

  // { 'YYYY-MM-DD': { open, total } }
  const byDay = useMemo(() => {
    const map = {};
    slots.forEach(s => {
      const k = dayKey(s.startTime);
      if (!map[k]) map[k] = { open: 0, total: 0 };
      map[k].total += 1;
      if (s.currentBookings < s.maxBookings) map[k].open += 1;
    });
    return map;
  }, [slots]);

  const year = month.getFullYear();
  const m = month.getMonth();
  const leading = (new Date(year, m, 1).getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < leading; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, m, d));

  const todayKey = dayKey(new Date());
  const selectedInfo = selectedDay ? byDay[selectedDay] : null;

  return (
    <div className="slot-cal">
      <div className="slot-cal__head">
        <button type="button" className="slot-cal__nav" aria-label="Previous month"
          onClick={() => setMonth(new Date(year, m - 1, 1))}>‹</button>
        <div className="slot-cal__title">
          {month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </div>
        <button type="button" className="slot-cal__nav" aria-label="Next month"
          onClick={() => setMonth(new Date(year, m + 1, 1))}>›</button>
      </div>

      <div className="slot-cal__grid">
        {WEEKDAYS.map(w => <div key={w} className="slot-cal__dow">{w}</div>)}
        {cells.map((date, i) => {
          if (!date) return <div key={`e${i}`} />;
          const key = dayKey(date);
          const info = byDay[key];
          const cls = [
            'slot-cal__day',
            info ? (info.open > 0 ? 'has-open' : 'has-full') : '',
            key === todayKey ? 'is-today' : '',
            key === selectedDay ? 'is-selected' : ''
          ].join(' ');
          return (
            <button
              type="button"
              key={key}
              className={cls}
              disabled={!info}
              onClick={() => onSelectDay(key === selectedDay ? null : key)}
              aria-label={`${date.toDateString()}${info ? `, ${info.total} session${info.total > 1 ? 's' : ''}` : ''}`}
            >
              <span className="slot-cal__num">{date.getDate()}</span>
              {info && (info.open > 0
                ? <b className="slot-cal__count">{info.open}</b>
                : <i className="slot-cal__dot" />)}
            </button>
          );
        })}
      </div>

      <div className="slot-cal__foot">
        <span className="slot-cal__legend"><i className="slot-cal__swatch slot-cal__swatch--open" /> open sessions</span>
        <span className="slot-cal__legend"><i className="slot-cal__swatch slot-cal__swatch--full" /> full</span>
        <span className="slot-cal__legend"><i className="slot-cal__swatch slot-cal__swatch--today" /> today</span>
        {selectedDay ? (
          <button type="button" className="slot-cal__clear" onClick={() => onSelectDay(null)}>
            {selectedInfo ? `${selectedInfo.total} session${selectedInfo.total > 1 ? 's' : ''} on this day · ` : ''}Show all
          </button>
        ) : (
          <span className="slot-cal__hint">Pick a day to see its sessions</span>
        )}
      </div>
    </div>
  );
}
